import {
  userCardAppCardQueryFn,
  useGetUserCardAppCardQueryKey,
} from "@-/dev.improve.haniverse.lib.cards.app-card";
import { cardReviewSettingsFromUser } from "@-/dev.improve.haniverse.lib.cards.card-content";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import {
  buildStudyCardDataList,
  type FsrsCard,
  type StudyCardData,
} from "#build-study-card-data-list";
import { STUDY_CARD_DATA_TABLES } from "#study-card-data-tables";

/**
 * The in-study cards of ONE subject (a grapheme, or a word) as {@link StudyCardData} — at most a
 * handful of rows: the meaning + pronunciation cards of a character, or the word cards of a word.
 *
 * This is what a single card's FSRS resolution needs, and it used to be served by loading the
 * WHOLE deck (every card, plus one app-card lookup per card).
 * That read sat on the drill page's critical path and, because the learn summary warms the drill
 * page in the background, competed with the summary's own first paint on a big deck. Scoping the
 * actor read to the subject's overlays in SQL makes it independent of the deck size.
 *
 * Keyed per subject; registered against the same tables as the whole-deck list so a rating or a
 * setting change invalidates it the same way.
 */
export function useGetSubjectStudyCardDataListQueryKey() {
  // The subject filter is applied at fetch time through this getter, so the `with` shape is bound
  // once here while each subject gets its own registered, write-clock guarded cache entry.
  const getSubjectUserCardsQueryKey = actorQueries(
    import.meta.location,
  ).userCard.findMany.useGetQueryKey({
    with: { fsrsCard: true },
  });
  const userSettingsQueryOptions = actorQueries(
    import.meta.location,
  ).user.findFirst.useQueryOptions({});
  const getUserCardAppCardQueryKey = useGetUserCardAppCardQueryKey();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(STUDY_CARD_DATA_TABLES));

  return (subject: string) =>
    withTableChangeVersion([
      "subjectStudyCardDataList",
      subject,
      { getSubjectUserCardsQueryKey, getUserCardAppCardQueryKey, userSettingsQueryOptions },
    ] as const);
}

export const [useSubjectStudyCardDataListQueryOptions, subjectStudyCardDataListQueryFn] =
  createUseQueryOptions(
    useGetSubjectStudyCardDataListQueryKey,
    async ({
      client,
      queryKey: [
        _,
        subject,
        { getSubjectUserCardsQueryKey, getUserCardAppCardQueryKey, userSettingsQueryOptions },
      ],
    }): Promise<StudyCardData[]> => {
      const [userCardList, userSettings] = await Promise.all([
        client.fetchQuery(
          actorQueries(import.meta.location).userCard.findMany.getQueryOptions(
            getSubjectUserCardsQueryKey,
            {
              where: {
                fsrsCardId: { isNotNull: true },
                // A subject is a grapheme (its meaning + pronunciation overlays carry it) or a
                // word (its word overlays do). The relation filters resolve to EXISTS subqueries
                // over the overlays' indexed `grapheme` / `word` columns.
                OR: [
                  { userGraphemeMeaningCard: { grapheme: subject } },
                  { userGraphemePronunciationCard: { grapheme: subject } },
                  { userWordSentenceCard: { word: subject } },
                ],
              },
            },
          ),
        ),
        client.fetchQuery(userSettingsQueryOptions),
      ]);

      // Only this subject's few cards resolve against the content catalog.
      const appCardByUserCardId = new Map(
        await Promise.all(
          userCardList.map(
            async (userCard) =>
              [
                userCard.id,
                await client.fetchQuery({
                  queryFn: userCardAppCardQueryFn,
                  queryKey: getUserCardAppCardQueryKey(userCard.id),
                }),
              ] as const,
          ),
        ),
      );

      const fsrsCardById = new Map<string, FsrsCard>();
      for (const userCard of userCardList) {
        if (userCard.fsrsCard !== null) {
          fsrsCardById.set(userCard.fsrsCard.id, userCard.fsrsCard);
        }
      }

      return buildStudyCardDataList({
        appCardByUserCardId,
        cardReviewSettings: cardReviewSettingsFromUser(userSettings),
        fsrsCardById,
        userCardList,
      });
    },
  );
